'use client'

import AuthGuard from '@/components/AuthGuard'
import { BreadcrumbNavigationProvider } from '@/components/navigation/breadcrumb-navigation-context'
import { NavigationGuardProvider } from '@/components/navigation/NavigationGuard'
import { Toaster } from '@/components/ui/sonner'
import { TooltipProvider } from '@/components/ui/tooltip'
import { createEntraIdClient } from '@/lib/entra-id'
import { useLocaleStore } from '@/store/useLocaleStore'
import { MsalProvider } from '@azure/msal-react'
import { GoogleOAuthProvider } from '@react-oauth/google'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { NextIntlClientProvider } from 'next-intl'
import { Suspense, useState } from 'react'
import type { AbstractIntlMessages } from 'next-intl'

// eslint-disable-next-line @typescript-eslint/no-require-imports
const messages: Record<string, AbstractIntlMessages> = {
  en: await import('../../messages/en.json'),
  th: await import('../../messages/th.json'),
}

function IntlProvider({ children }: { children: React.ReactNode }) {
  const locale = useLocaleStore((s) => s.locale)
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone

  return (
    <NextIntlClientProvider locale={locale} messages={messages[locale]} timeZone={timeZone}>
      {children}
    </NextIntlClientProvider>
  )
}

export default function Providers({ children }: { children: React.ReactNode }) {
  const [entraIdClient] = useState(createEntraIdClient)
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            retry: 1,
            refetchOnWindowFocus: false,
          },
        },
      }),
  )

  let app = (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Suspense fallback={null}>
          <AuthGuard>
            <NavigationGuardProvider>
              <BreadcrumbNavigationProvider>{children}</BreadcrumbNavigationProvider>
            </NavigationGuardProvider>
          </AuthGuard>
        </Suspense>
        <Toaster />
      </TooltipProvider>
    </QueryClientProvider>
  )

  if (process.env.NEXT_PUBLIC_GOOGLE_OAUTH2_CLIENT_ID) {
    const googleClientId = process.env.NEXT_PUBLIC_GOOGLE_OAUTH2_CLIENT_ID ?? ''
    app = <GoogleOAuthProvider clientId={googleClientId}>{app}</GoogleOAuthProvider>
  }

  if (entraIdClient) {
    app = <MsalProvider instance={entraIdClient}>{app}</MsalProvider>
  }

  return <IntlProvider>{app}</IntlProvider>
}
